import React, { useState, useEffect } from "react";
import { api } from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ConfirmationModal from "../common/ConfirmationModal";
import EmailPreviewModal from "./EmailPreviewModal";
import NotificationSettingsCard from "./NotificationSettingsCard";

const EmailHistoryPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devToolsOpen, setDevToolsOpen] = useState(false);

  // Mail Debug State
  const [testEmail, setTestEmail] = useState("");
  const [mailLoading, setMailLoading] = useState(false);
  const [mailFeedback, setMailFeedback] = useState(null);

  // Preview Modal
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    previews: [],
    type: null, // 'MONTHLY' or 'SEMESTER'
    isMock: false,
  });

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null, // 'SINGLE' or 'ALL'
    id: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get("/email-logs");
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch email logs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestWarning = async () => {
    if (!testEmail) {
      setMailFeedback({
        type: "error",
        message: "Please enter an email address",
      });
      setTimeout(() => setMailFeedback(null), 3000);
      return;
    }
    try {
      setMailLoading(true);
      await api.post(`/debug/mail/test-warning?to=${testEmail}`);
      setMailFeedback({
        type: "success",
        message: `Test warning sent to ${testEmail}`,
      });
      setTestEmail("");
      // Refresh logs after a short delay
      setTimeout(fetchLogs, 1000);
    } catch (error) {
      console.error("Mail test error:", error);
      setMailFeedback({
        type: "error",
        message: "Failed to send warning email",
      });
    } finally {
      setMailLoading(false);
      setTimeout(() => setMailFeedback(null), 3000);
    }
  };

  const handlePreviewReport = async (isMock = false) => {
    try {
      setMailLoading(true);
      setMailFeedback(null);
      const endpoint = isMock
        ? "/debug/mail/preview-report?mock=true"
        : "/debug/mail/preview-report";
      const response = await api.post(endpoint);
      setPreviewModal({
        isOpen: true,
        previews: response.data,
        type: "MONTHLY",
        isMock: isMock,
      });
    } catch (error) {
      console.error("Preview error:", error);
      setMailFeedback({
        type: "error",
        message: "Failed to generate report preview",
      });
    } finally {
      setMailLoading(false);
    }
  };

  const handlePreviewSemesterReport = async (isMock = false) => {
    try {
      setMailLoading(true);
      setMailFeedback(null);
      const endpoint = isMock
        ? "/debug/mail/preview-semester-report?mock=true"
        : "/debug/mail/preview-semester-report";
      const response = await api.post(endpoint);
      setPreviewModal({
        isOpen: true,
        previews: response.data,
        type: "SEMESTER",
        isMock: isMock,
      });
    } catch (error) {
      console.error("Preview error:", error);
      setMailFeedback({
        type: "error",
        message: "Failed to generate semester report preview",
      });
    } finally {
      setMailLoading(false);
    }
  };

  const handleConfirmSend = async (overrideRecipient = null) => {
    try {
      setMailLoading(true);
      let endpoint =
        previewModal.type === "MONTHLY"
          ? "/debug/mail/trigger-report"
          : "/debug/mail/trigger-semester-report";

      const params = new URLSearchParams();
      if (previewModal.isMock) {
        params.append("mock", "true");
      }
      if (overrideRecipient) {
        params.append("recipientOverride", overrideRecipient);
      }

      if ([...params].length > 0) {
        endpoint += "?" + params.toString();
      }

      const response = await api.post(endpoint);

      setMailFeedback({
        type: "success",
        message: response.data || "Report triggered successfully",
      });
      setPreviewModal({
        isOpen: false,
        previews: [],
        type: null,
        isMock: false,
      });
      setTimeout(fetchLogs, 1000);
    } catch (error) {
      console.error("Trigger error:", error);
      setMailFeedback({
        type: "error",
        message: "Failed to trigger report sending",
      });
    } finally {
      setMailLoading(false);
      setTimeout(() => setMailFeedback(null), 5000);
    }
  };

  const handleDeleteHistory = () => {
    setDeleteModal({
      isOpen: true,
      type: "ALL",
      id: null,
    });
  };

  const handleDeleteLog = (id, e) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      type: "SINGLE",
      id: id,
    });
  };

  const executeDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteModal.type === "ALL") {
        await api.delete("/email-logs");
        setLogs([]);
      } else if (deleteModal.type === "SINGLE" && deleteModal.id) {
        await api.delete(`/email-logs/${deleteModal.id}`);
        setLogs(logs.filter((log) => log.id !== deleteModal.id));
      }
      setDeleteModal({ isOpen: false, type: null, id: null });
    } catch (error) {
      console.error("Failed to delete log(s)", error);
      alert("Failed to delete. Please check permissions.");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading email history..." />;

  return (
    <div className="min-h-screen bg-green-cloud">
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, type: null, id: null })}
        onConfirm={executeDelete}
        title={
          deleteModal.type === "ALL"
            ? "Clear Log History?"
            : "Delete Log Entry?"
        }
        message={
          deleteModal.type === "ALL"
            ? "Are you sure you want to delete all email history? This action cannot be undone."
            : "Are you sure you want to delete this log entry? It will be removed permanently."
        }
        confirmText="Delete"
        isDanger={true}
        isLoading={deleteLoading}
      />

      <EmailPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
        onConfirm={handleConfirmSend}
        previews={previewModal.previews}
        isLoading={mailLoading}
        currentUserEmail={user?.email}
        title={
          previewModal.type === "MONTHLY"
            ? "Confirm Monthly Report"
            : "Confirm Semester Report"
        }
        isMock={previewModal.isMock}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-history mr-3 text-sunrise-red"></i>
                  Email History
                </h1>
                <p className="text-sm md:text-base text-slate-cloud">
                  Monitor sent emails and trigger manual reports
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex items-center gap-2">
                <button
                  onClick={() => setDevToolsOpen((v) => !v)}
                  aria-expanded={devToolsOpen}
                  title="Developer tools"
                  className={`w-9 h-9 rounded-lg border text-sm transition-colors flex items-center justify-center ${
                    devToolsOpen
                      ? "border-sunrise-red/40 bg-sunrise-red/10 text-sunrise-red"
                      : "border-pale-dawn bg-white text-slate-cloud hover:text-grey-dusk"
                  }`}
                >
                  <i
                    className={`fas ${
                      mailLoading ? "fa-circle-notch fa-spin" : "fa-terminal"
                    }`}
                  ></i>
                </button>
                <button
                  onClick={handleDeleteHistory}
                  className="bg-sunrise-red hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
                >
                  <i className="fas fa-trash-alt"></i>
                  Clear History
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Global Feedback */}
        {mailFeedback && (
          <div
            className={`mb-8 p-4 rounded-lg text-sm flex items-center gap-3 shadow-sm ${
              mailFeedback.type === "success"
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
            } animate-fade-in`}
          >
            <i
              className={`fas ${
                mailFeedback.type === "success"
                  ? "fa-check-circle"
                  : "fa-exclamation-circle"
              } text-lg`}
            ></i>
            <span className="font-medium">{mailFeedback.message}</span>
          </div>
        )}
        {/* Personal notification settings */}
        <NotificationSettingsCard />

        {/* Developer Tools — toggled by the small terminal button in the header */}
        {devToolsOpen && (
          <div className="card-brand mb-8 overflow-hidden">
            <div className="px-5 py-2 bg-ice-white border-b border-pale-dawn flex items-center gap-2 text-[11px] text-slate-cloud">
              <i className="fas fa-terminal"></i>
              <span className="font-semibold text-grey-dusk">
                Developer Tools
              </span>
              <span>
                — preview templates with mock data and simulate warning emails.
              </span>
            </div>

            <div className="p-5 bg-white">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Previews */}
                <div className="lg:col-span-2">
                  <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    <i className="fas fa-eye text-sunrise-red"></i>
                    Preview (Mock Data)
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => handlePreviewReport(true)}
                      disabled={mailLoading}
                      className="flex-1 h-10 px-4 rounded-xl bg-white border border-pale-dawn text-grey-dusk font-semibold text-xs hover:border-sunrise-red/30 hover:shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-vial text-slate-cloud"></i>
                      Monthly Report
                    </button>

                    <button
                      onClick={() => handlePreviewSemesterReport(true)}
                      disabled={mailLoading}
                      className="flex-1 h-10 px-4 rounded-xl bg-white border border-pale-dawn text-grey-dusk font-semibold text-xs hover:border-sunrise-red/30 hover:shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      <i className="fas fa-vial text-slate-cloud"></i>
                      Semester Report
                    </button>
                  </div>
                </div>

                {/* Simulate Warning */}
                <div className="lg:col-span-1">
                  <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-2">
                    <i className="fas fa-paper-plane text-sunrise-red"></i>
                    Simulate Warning
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <i className="fas fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                      <input
                        type="email"
                        placeholder="Recipient email"
                        className="input-brand w-full pl-9 h-10 text-xs bg-white"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                      />
                    </div>

                    <button
                      onClick={handleSendTestWarning}
                      disabled={mailLoading || !testEmail}
                      className="h-10 px-4 rounded-xl bg-sunrise-red text-white font-semibold text-xs hover:bg-sunrise-600 transition-colors disabled:opacity-60 flex items-center gap-2"
                      title={
                        !testEmail
                          ? "Enter an email first"
                          : "Send test warning"
                      }
                    >
                      <i className="fas fa-paper-plane"></i>
                      Send
                    </button>
                  </div>

                  <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-2">
                    <i className="fas fa-circle-info text-sunrise-red"></i>
                    Sends mock data only. No production recipients.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Production Reports (Primary) */}
        <div className="bg-white rounded-xl shadow-brand border border-pale-dawn mb-8 overflow-hidden transform transition-all hover:shadow-lg">
          <div className="p-6 border-b border-pale-dawn bg-white">
            <h2 className="text-xl font-bold text-grey-dusk flex items-center gap-2">
              <i className="fas fa-file-invoice text-slate-700"></i>
              Production Reports
            </h2>
            <p className="text-sm text-slate-cloud mt-1">
              Generate official reports based on real learner data.
            </p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monthly Report Action */}
            <div className="p-5 rounded-lg border border-pale-dawn bg-ice-white hover:border-slate-300 transition-colors group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <i className="fas fa-calendar-alt text-6xl text-slate-400 transform rotate-12"></i>
              </div>
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="w-12 h-12 rounded-lg bg-white shadow-sm border border-pale-dawn flex items-center justify-center text-slate-600 group-hover:text-red-600 transition-colors">
                  <i className="fas fa-calendar-day text-xl"></i>
                </div>
              </div>
              <div className="relative z-10">
                <h3 className="font-bold text-grey-dusk mb-1 text-lg">
                  Monthly Report
                </h3>
                <p className="text-sm text-slate-cloud mb-5">
                  Generate the report for the current month.
                </p>
                <button
                  onClick={() => handlePreviewReport(false)}
                  disabled={mailLoading}
                  className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 hover:text-slate-900 rounded-md text-sm font-semibold transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  {mailLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin text-slate-400"></i>
                      <span className="text-slate-500">Processing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play text-xs text-slate-400 group-hover:text-red-500 transition-colors"></i>
                      Generate Monthly Report
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Semester Report Action */}
            <div className="p-5 rounded-lg border border-pale-dawn bg-ice-white hover:border-slate-300 transition-colors group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <i className="fas fa-graduation-cap text-6xl text-slate-400 transform -rotate-12"></i>
              </div>
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="w-12 h-12 rounded-lg bg-white shadow-sm border border-pale-dawn flex items-center justify-center text-slate-600 group-hover:text-red-600 transition-colors">
                  <i className="fas fa-user-graduate text-xl"></i>
                </div>
              </div>
              <div className="relative z-10">
                <h3 className="font-bold text-grey-dusk mb-1 text-lg">
                  Semester Report
                </h3>
                <p className="text-sm text-slate-cloud mb-5">
                  Generate the report for the last semester (6 months).
                </p>
                <button
                  onClick={() => handlePreviewSemesterReport(false)}
                  disabled={mailLoading}
                  className="w-full py-2.5 px-4 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 hover:text-slate-900 rounded-md text-sm font-semibold transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  {mailLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin text-slate-400"></i>
                      <span className="text-slate-500">Processing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play text-xs text-slate-400 group-hover:text-red-500 transition-colors"></i>
                      Generate Semester Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Email Logs Table */}
        <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn overflow-hidden">
          <div className="px-6 py-4 border-b border-pale-dawn bg-pale-dawn/10">
            <h3 className="font-bold text-grey-dusk">Recent Activity</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-pale-dawn">
              <thead className="bg-pale-dawn/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-cloud uppercase tracking-wider">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ice-white divide-y divide-pale-dawn">
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-pale-dawn/10 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            log.status === "SENT"
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : "bg-red-100 text-red-800 border border-red-200"
                          }`}
                        >
                          {log.status === "SENT" ? (
                            <i className="fas fa-check mr-1"></i>
                          ) : (
                            <i className="fas fa-times mr-1"></i>
                          )}
                          {log.status}
                        </span>
                        {log.errorMessage && (
                          <div
                            className="text-xs text-red-600 mt-1 max-w-xs truncate"
                            title={log.errorMessage}
                          >
                            {log.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-cloud">
                        {new Date(log.sentAt).toLocaleString("de-CH", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-grey-dusk">
                        {log.recipient}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-dusk">
                        {log.subject}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate"
                        title={log.bodyPreview}
                      >
                        <span className="font-mono text-xs text-slate-400">
                          {log.bodyPreview?.substring(0, 40)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => handleDeleteLog(log.id, e)}
                          className="text-pale-dawn hover:text-red-500 transition-colors"
                          title="Delete Log"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-slate-cloud"
                    >
                      <i className="fas fa-inbox text-4xl mb-3 opacity-30"></i>
                      <p>No emails sent yet.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailHistoryPage;
